import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import Mascot from "@/components/Mascot";
import { Mail, Lock, Eye, EyeOff, ArrowRight } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { api, setAuthToken } from "@/lib/http";
import { isIOSNative, isAndroidNative } from "@/lib/platform";
import { authErrorKey } from "@/lib/authErrors";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const GoogleG = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
    <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.1 18.9 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.2 26.7 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/>
  </svg>
);

const Leaf = ({ className, flip }) => (
  <svg viewBox="0 0 120 220" className={className} style={flip ? { transform: "scaleX(-1)" } : undefined} aria-hidden>
    <g fill="none" stroke="#7BA8A0" strokeWidth="3" opacity="0.35">
      <path d="M60 210 C 40 150, 40 80, 60 10" />
      <path d="M60 60 C 40 55, 25 40, 20 20" />
      <path d="M60 100 C 38 95, 20 78, 14 55" />
      <path d="M60 140 C 36 135, 16 116, 10 90" />
      <path d="M60 60 C 80 55, 95 40, 100 20" />
      <path d="M60 100 C 82 95, 100 78, 106 55" />
      <path d="M60 140 C 84 135, 104 116, 110 90" />
    </g>
  </svg>
);

export default function LoginPage() {
  const { t } = useT();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // Suspended account: persistent notice (set by a 403 ACCOUNT_BLOCKED on login
  // or by the http interceptor redirecting here with ?blocked=1).
  const [blocked, setBlocked] = useState(() => new URLSearchParams(window.location.search).get("blocked") === "1");

  // The login screen is designed light-only. The ThemeProvider (parent)
  // applies the `dark` class AFTER this child effect runs, so we keep it
  // stripped with an observer and restore the user's theme on unmount.
  useEffect(() => {
    const root = document.documentElement;
    const strip = () => { if (root.classList.contains("dark")) root.classList.remove("dark"); };
    strip();
    const obs = new MutationObserver(strip);
    obs.observe(root, { attributes: true, attributeFilter: ["class"] });
    return () => {
      obs.disconnect();
      if (root.getAttribute("data-theme") === "dark") root.classList.add("dark");
    };
  }, []);

  const handleGoogle = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const isIOSNativeApp = isIOSNative();
  // Google blocks OAuth inside Android WebViews ("disallowed_useragent") —
  // hide the button in the native Android app; email login always works.
  const hideGoogle = isIOSNativeApp || isAndroidNative();
  const [appleLoading, setAppleLoading] = useState(false);

  const finishLogin = (data) => {
    if (data.token) setAuthToken(data.token);
    setUser(data.user);
    const params = new URLSearchParams(window.location.search);
    const next = params.get("next");
    if (next && next.startsWith("/")) { navigate(next, { replace: true }); return; }
    if (!data.user.role) navigate("/onboarding", { replace: true });
    else navigate(data.user.role === "super_admin" ? "/admin" : data.user.role === "nutritionist" || data.user.role === "coach" ? "/nutrizionista" : "/app", { replace: true });
  };

  const handleApple = async () => {
    if (appleLoading) return;
    setAppleLoading(true);
    try {
      const { SignInWithApple } = await import("@capacitor-community/apple-sign-in");
      const nonce = Math.random().toString(36).slice(2) + Date.now().toString(36);
      const result = await SignInWithApple.authorize({
        clientId: "com.solano.essence",
        redirectURI: "https://solano-essence.com",
        scopes: "email name",
        nonce,
      });
      const idToken = result?.response?.identityToken;
      if (!idToken) throw new Error("no_token");
      const { data } = await api.post("/auth/apple", {
        identity_token: idToken,
        nonce,
        email: result.response.email || null,
        given_name: result.response.givenFamilyName || result.response.givenName || null,
        family_name: result.response.familyName || null,
      });
      finishLogin(data);
    } catch (err) {
      const msg = String(err?.message || err?.errorMessage || "").toLowerCase();
      if (!msg.includes("cancel") && !msg.includes("1001")) {
        const d = err?.response?.data?.detail;
        const key = authErrorKey(d);
        if (key) toast.error(t(key));
        else toast.error(typeof d === "string" ? d : t("common.error"));
      }
    } finally {
      setAppleLoading(false);
    }
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.token) setAuthToken(data.token);
      setUser(data.user);
      // Honor ?next= if present
      const params = new URLSearchParams(window.location.search);
      const next = params.get("next");
      if (next && next.startsWith("/")) { navigate(next, { replace: true }); return; }
      if (!data.user.role) navigate("/onboarding", { replace: true });
      else navigate(data.user.role === "super_admin" ? "/admin" : data.user.role === "nutritionist" || data.user.role === "coach" ? "/nutrizionista" : "/app", { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      const key = authErrorKey(d);
      if (d === "ACCOUNT_BLOCKED") { setBlocked(true); return; }
      if (!err.response) toast.error(t("common.networkError"));
      else if (key) toast.error(t(key));
      else if (err.response.status === 401) toast.error(t("login.invalidCredentials"));
      else toast.error(typeof d === "string" ? d : `${t("common.error")} (${err.response.status})`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF6EC] relative overflow-hidden flex flex-col">
      {/* Header: logo + language pill */}
      <header className="relative z-10 flex items-center justify-between px-5 sm:px-8 max-w-5xl w-full mx-auto" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)" }}>
        <Logo />
        <div className="rounded-full bg-white shadow-sm border border-slate-100 px-1 py-0.5">
          <LanguageSwitcher />
        </div>
      </header>

      <main className="relative z-10 flex-1 flex flex-col items-center px-6 pb-6 w-full max-w-md mx-auto">
        {/* Mascot with soft glow + leaves */}
        <div className="relative mt-2 mb-1 h-36 w-full flex items-center justify-center">
          <span className="absolute h-32 w-32 rounded-full bg-gradient-to-br from-[#F4E7CE] via-white to-[#DCEDE9] blur-[2px] shadow-[0_20px_60px_-20px_rgba(90,139,143,0.35)]" aria-hidden />
          <Leaf className="absolute left-[-6px] bottom-0 h-24 w-16 opacity-50" flip />
          <Leaf className="absolute right-[-6px] top-1 h-28 w-16 opacity-50" />
          <Mascot size={104} pose="static" alt="Solano" className="relative drop-shadow-xl" />
        </div>

        <h1 className="font-editorial text-[1.9rem] leading-tight text-[#22333B] text-center" data-testid="login-title">
          {t("login.title").replace(/\.$/, "")}
        </h1>
        <p className="mt-1 text-center text-slate-500 text-[13px] leading-snug max-w-xs">
          {t("login.welcomeSub")}
        </p>

        {/* Email / password form */}
        {blocked && (
          <div role="alert" className="w-full mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-left" data-testid="login-blocked-notice">
            <p className="text-sm font-semibold text-rose-800">{t("auth.blockedTitle")}</p>
            <p className="mt-1 text-xs leading-relaxed text-rose-700">{t("auth.blockedBody")}</p>
            <a href="mailto:info@solano-essence.com?subject=Kontosperre%20Solano%20Essence" data-testid="login-blocked-contact"
              className="mt-2 inline-flex items-center justify-center h-9 px-4 rounded-full bg-rose-600 text-white text-xs font-semibold hover:bg-rose-700 transition">
              {t("auth.blockedCta")}
            </a>
          </div>
        )}

        <form onSubmit={handleEmailLogin} className="w-full mt-4 space-y-3">
          <div>
            <label htmlFor="email" className="block text-[13px] font-semibold text-[#22333B] mb-1">
              {t("login.emailLabel")}
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-slate-400" />
              <input
                id="email" type="email" required value={email} data-testid="login-email"
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("login.emailPlaceholder")}
                className="w-full h-[46px] rounded-2xl border border-slate-200 bg-white pl-11 pr-4 text-[15px] text-[#22333B] placeholder:text-slate-400 shadow-sm outline-none focus:border-[#5A8B8F] focus:ring-2 focus:ring-[#5A8B8F]/25 transition"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-[13px] font-semibold text-[#22333B] mb-1">
              {t("login.passwordLabel")}
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-[18px] w-[18px] text-slate-400" />
              <input
                id="password" type={showPw ? "text" : "password"} required value={password} data-testid="login-password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••"
                className="w-full h-[46px] rounded-2xl border border-slate-200 bg-white pl-11 pr-12 text-[15px] text-[#22333B] placeholder:text-slate-400 shadow-sm outline-none focus:border-[#5A8B8F] focus:ring-2 focus:ring-[#5A8B8F]/25 transition"
              />
              <button
                type="button" tabIndex={-1} onClick={() => setShowPw((v) => !v)}
                data-testid="login-password-toggle" aria-label="toggle password"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition"
              >
                {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit" disabled={loading} data-testid="login-submit-btn"
            className="relative w-full h-[48px] rounded-full bg-[#2C6467] hover:bg-[#245356] text-white text-base font-semibold shadow-lg shadow-[#2C6467]/25 active:scale-[0.99] transition disabled:opacity-70 flex items-center justify-center !mt-4"
          >
            {loading ? "…" : t("login.loginBtn")}
            <ArrowRight className="h-5 w-5 absolute right-6" />
          </button>
        </form>

        <button
          type="button" onClick={() => navigate("/forgot-password")} data-testid="forgot-password-link"
          className="mt-2.5 text-[#3D7A7E] font-medium hover:underline text-sm"
        >
          {t("login.forgotPassword")}
        </button>

        {/* Divider */}
        <div className="w-full flex items-center gap-4 my-3">
          <span className="h-px flex-1 bg-slate-200" />
          <span className="text-slate-400 text-xs">{t("login.hintOr")}</span>
          <span className="h-px flex-1 bg-slate-200" />
        </div>

        {isIOSNativeApp && (
          <button
            type="button" onClick={handleApple} disabled={appleLoading} data-testid="login-apple-btn"
            className="w-full h-[46px] rounded-full bg-black text-white text-[15px] font-semibold flex items-center justify-center gap-3 active:scale-[0.99] transition disabled:opacity-60"
          >
            <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true"><path d="M16.365 12.79c-.024-2.44 1.995-3.61 2.086-3.668-1.136-1.662-2.905-1.89-3.534-1.915-1.504-.152-2.935.886-3.698.886-.762 0-1.94-.864-3.19-.84-1.641.024-3.153.954-3.997 2.423-1.704 2.955-.435 7.33 1.224 9.729.812 1.174 1.78 2.492 3.05 2.445 1.224-.05 1.686-.792 3.166-.792 1.48 0 1.895.792 3.19.767 1.318-.024 2.153-1.198 2.96-2.377.932-1.363 1.316-2.683 1.34-2.751-.03-.013-2.572-.987-2.597-3.907zM13.9 5.62c.675-.817 1.13-1.953 1.006-3.084-.972.04-2.148.647-2.845 1.463-.624.723-1.171 1.878-1.024 2.987 1.083.084 2.19-.55 2.863-1.366z"/></svg>
            {appleLoading ? "…" : t("login.appleBtn")}
          </button>
        )}

        {/* Google OAuth needs the system browser — not allowed on iOS (Guideline 4).
            Same-email accounts unify, so Apple Sign-In reaches the same account. */}
        {!hideGoogle && (
          <button
            type="button" onClick={handleGoogle} data-testid="login-google-btn"
            className="w-full h-[46px] rounded-full bg-white border border-slate-200 shadow-sm hover:shadow-md text-[#22333B] text-[15px] font-semibold flex items-center justify-center gap-3 active:scale-[0.99] transition"
          >
            <GoogleG /> {t("login.googleBtn")}
          </button>
        )}

        <p className="mt-3 text-sm text-[#22333B]">
          {t("login.noAccount")}{" "}
          <button
            type="button" onClick={() => navigate("/register")} data-testid="go-register-link"
            className="text-[#E8951C] font-bold hover:underline"
          >
            {t("login.registerLink")}
          </button>
        </p>
      </main>
    </div>
  );
}
